/**
 * API Client — Axios-Instanz mit Interceptors
 *
 * Die Anmeldung läuft über ein httpOnly-Cookie, das der Browser dank `withCredentials`
 * mitschickt. Es gibt hier deshalb KEINEN Authorization-Header und kein Token im Speicher:
 * was JavaScript nicht lesen kann, kann eine XSS auch nicht stehlen.
 *
 * Voraussetzung dafür ist, dass die API unter derselben Origin erreichbar ist — in der
 * Entwicklung über den Vite-Proxy, in Produktion über `location /api/` im nginx des
 * PWA-Containers. `VITE_API_URL` ist deshalb `/api`.
 */

import axios, { AxiosError, type AxiosInstance } from 'axios'
import { useAuthStore } from '@/stores/auth.store'
import type { ApiError } from '@/types/api.types'

/**
 * Create configured Axios instance
 */
function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    timeout: 30000,
    // Ohne dieses Flag schickt Axios das Sitzungscookie nicht mit.
    withCredentials: true,
    headers: {
      // No explicit Content-Type! Axios auto-sets:
      //   application/json   → for object payloads
      //   multipart/form-data → for FormData (with boundary)
      // A default Content-Type here would override FormData auto-detection → 415.
      'Accept-Language': 'de-DE',
      'X-Version': '1.0',
    },
  })

  // Request Interceptor — Block requests when offline (fail fast)
  client.interceptors.request.use((config) => {
    if (!navigator.onLine) {
      return Promise.reject(
        new AxiosError('Offline — Anfrage übersprungen', AxiosError.ERR_NETWORK, config)
      )
    }
    return config
  })

  // Request Interceptor — Protokollierung in der Entwicklung.
  //
  // Hier stand bis zum 2026-09-02 die Token-Einschleusung (`Authorization: Bearer`) und ein
  // `X-Session-Id`-Kopf. Beides ist entfallen: das Cookie trägt die Sitzung, und die
  // Sitzungskennung steht als Anspruch im Token, das der Server selbst liest.
  client.interceptors.request.use(
    (config) => {
      // Log requests in development
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`)
      }

      return config
    },
    (error) => {
      console.error('[API] Request error:', error)
      return Promise.reject(error)
    }
  )

  // Response Interceptor - Handle Errors
  client.interceptors.response.use(
    (response) => {
      // Log responses in development
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log(`[API] Response ${response.status}:`, response.config.url)
      }
      return response
    },
    async (error: AxiosError<ApiError>) => {
      const authStore = useAuthStore()

      // Handle 401 Unauthorized
      // Skip when offline — the 401 might be a stale SW-cached response.
      // Logging out would destroy the local session and make the app unusable
      // offline. Session will be re-validated on the next online navigation.
      //
      // Auth-Routen sind ausgenommen, und zwar aus einem gemessenen Grund: auf
      // `/Auth/login` bedeutet 401 „falsche Zugangsdaten", nicht „Sitzung abgelaufen".
      // Der harte `window.location.href` darunter lud die Seite neu und riss damit den
      // Pinia-State weg — samt der Fehlermeldung, die `LoginView` bereits gesetzt hatte.
      // Am 2026-08-22 gemessen: falsches Passwort ergab 401, fuenf Navigationen auf /login
      // und **keine** Rueckmeldung; wer sich vertippte, sah nur ein leeres Formular.
      // `me` und `logout` behandeln ihren Fehler ebenfalls selbst (`clearAuthData()` bzw.
      // stilles Verwerfen), der Router-Guard leitet danach um. Auf `/Auth/me` ist 401 sogar die
      // reguläre Antwort für „nicht angemeldet" — ein harter Rauswurf wäre dort schlicht falsch.
      const pfad = error.config?.url ?? ''
      const istAuthRoute = /\/Auth\/(login|logout|me)/.test(pfad)

      if (error.response?.status === 401 && navigator.onLine && !istAuthRoute) {
        console.warn('[API] Unauthorized - logging out')
        await authStore.logout()

        // Redirect to login (handled by router guard)
        window.location.href = '/login'
        return Promise.reject(error)
      }

      // Handle 403 Forbidden
      if (error.response?.status === 403) {
        console.warn('[API] Forbidden - insufficient permissions')
      }

      // Log errors in development (suppress expected offline noise)
      if (import.meta.env.DEV && navigator.onLine) {
        if (!error.response) {
          console.error('[API] Network error:', error.message)
        }
        console.error('[API] Error:', {
          status: error.response?.status,
          url: error.config?.url,
          message: error.response?.data?.title || error.message,
        })
      }

      return Promise.reject(error)
    }
  )

  return client
}

/**
 * Singleton API client instance
 */
export const apiClient = createApiClient()

/**
 * Helper to extract error message from API error
 */
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const apiError = error.response?.data as ApiError | undefined

    if (apiError?.title) {
      return apiError.title
    }

    if (apiError?.detail) {
      return apiError.detail
    }

    // Klartextfeld dieses Backends — wird unveraendert angezeigt.
    //
    // Hier stand bis 2026-09-02 eine Tabelle, die `InvalidCredentials` und
    // `UsernamePasswordRequired` in Saetze uebersetzte, weil das Backend die rohen
    // Ressourcenschluessel schickte. Sie ist entfernt, und zwar nicht nur weil sie
    // gegenstandslos wurde: sie hat den eigentlichen Fehler ueber Wochen verdeckt. Die
    // Lokalisierung des Backends war falsch verdrahtet (`ResourcesPath` verdoppelte den
    // Namespace-Teil), was niemandem auffiel, weil die PWA genau diese Schluessel abfing.
    // Behoben backend-seitig mit 619abea und am 2026-09-02 auf Prod nachgemessen.
    //
    // Ohne die Tabelle wird ein kuenftiger roher Schluessel sofort sichtbar — das ist der
    // Sinn der Sache, nicht ein Schoenheitsfehler.
    if (apiError?.message) {
      return apiError.message
    }

    if (error.message === 'Network Error') {
      return 'Netzwerkfehler - Bitte überprüfen Sie Ihre Internetverbindung'
    }

    return error.message
  }

  if (error instanceof Error) {
    return error.message
  }

  return 'Ein unbekannter Fehler ist aufgetreten'
}

/**
 * Helper to check if error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    return !error.response
  }
  return false
}

export default apiClient
